import React, { useRef } from 'react';
import {
  useDrag,
  useDrop,
  DropTargetMonitor,
  DragSourceMonitor,
} from 'react-dnd';
import { XYCoord } from 'dnd-core';
import styles from '../../styles/components/drag-and-drop/DragCard.module.css';
import UUID from '@ax2/common/model/UUID';
import RemoveAction, { RemoveActionProps } from './actions/RemoveAction';
import moveDots from '@/assets/move_dots.svg';

export interface DragItemId {
  id: UUID;
}

export interface Card<T extends DragItemId> {
  itemControl?: string | JSX.Element;
  orderControl?: (order: number) => string | JSX.Element;
  typeIcon?: string;
  withTextFieldPlaceholder?: boolean; // Adds fake textfield-like element to the left of an item
  dragZone?: 'item' | 'icon' | 'none';
  itemFrame?: 'border' | 'shadow' | 'default';
  isEditable?: boolean;
  item: T;
}

export enum DragCardType {
  ITEM = 'item',
}

export interface DragCardActions<T extends DragItemId> {
  onMove: (dragIndex: number, hoverIndex: number) => void;
  removeAction?: RemoveActionProps<T>;
}

export interface DragCardProps<T extends DragItemId>
  extends Card<T>,
    DragCardActions<T> {
  index: number;
}

interface DragCard {
  id: string;
  index: number;
  type: DragCardType;
}

const DragCard = <T extends DragItemId>({
  index,
  itemControl,
  orderControl,
  typeIcon,
  withTextFieldPlaceholder = false,
  dragZone = 'item',
  itemFrame = 'default',
  isEditable = true,
  item,
  removeAction,
  onMove,
}: DragCardProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: DragCardType.ITEM,
    hover: (item: DragCard, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect =
        ref.current && ref.current.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: { id: item.id, index, type: DragCardType.ITEM },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));
  return (
    <div key={item.id} className={styles.container}>
      <div
        className={styles.content}
        ref={dragZone === 'item' ? ref : preview}
        style={{
          opacity: isDragging ? 0 : 1,
          cursor: dragZone === 'item' ? 'move' : 'default',
        }}
      >
        {withTextFieldPlaceholder && (
          <div className={styles.fakeTextField}></div>
        )}
        <div
          className={`${styles[itemFrame + 'Item']} ${
            isEditable ? styles.editableItem : ''
          }`}
        >
          {dragZone !== 'none' && (
            <div
              className={styles.dragIcon}
              ref={dragZone === 'icon' ? ref : null}
              style={{ cursor: dragZone === 'icon' ? 'move' : 'inherit' }}
            >
              <img src={moveDots} alt="move" className={styles.moveIcon} />
            </div>
          )}
          {typeIcon && (
            <div className={styles.typeIcon}>
              <img src={typeIcon} alt="type" />
            </div>
          )}
          {orderControl && (
            <div className={styles.orderControl}>{orderControl(index)}</div>
          )}
          <div className={styles.itemControl}>{itemControl}</div>
        </div>
        {removeAction && <RemoveAction {...{ ...removeAction, item }} />}
      </div>
    </div>
  );
};

export default DragCard;
